import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { getTitleAfterHyphen } from '../../util/string';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_NIGHT,
  LINE_ITEM_OFFER,
  LINE_ITEM_REQUEST,
  propTypes,
} from '../../util/types';

import css from './OrderBreakdown.module.css';
import classNames from 'classnames';

/**
 * A component that renders the base price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {propTypes.lineItemUnitType} props.code - The code of the line item
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemBasePriceMaybe = props => {
  const { lineItems, code, intl, isMultipleDay, transaction } = props;
  const isNightly = code === LINE_ITEM_NIGHT;
  const isDaily = code === LINE_ITEM_DAY;
  const isHourly = code === LINE_ITEM_HOUR;
  const isFixed = code === LINE_ITEM_FIXED;
  const isRequest = code === LINE_ITEM_REQUEST;
  const isOffer = code === LINE_ITEM_OFFER;
  const translationKey = isNightly
    ? 'OrderBreakdown.baseUnitNight'
    : isDaily
    ? 'OrderBreakdown.baseUnitDay'
    : isHourly
    ? 'OrderBreakdown.baseUnitHour'
    : isFixed
    ? 'OrderBreakdown.baseUnitFixedBooking'
    : isRequest
    ? 'OrderBreakdown.baseUnitRequest'
    : isOffer
    ? 'OrderBreakdown.baseUnitOffer'
    : 'OrderBreakdown.baseUnitQuantity';

  // Find correct line-item for given code prop.
  // It should be one of the following: 'line-item/night, 'line-item/day', 'line-item/hour', 'line-item/fixed', 'line-item/item', 'line-item/offer', 'line-item/request'
  // These are defined in '../../util/types';
  const unitPurchase = lineItems.find(item => item.code === code && !item.reversal);

  const quantity = unitPurchase?.units
    ? unitPurchase.units.toString()
    : unitPurchase?.quantity
    ? unitPurchase.quantity.toString()
    : null;
  const unitPrice = unitPurchase ? formatMoney(intl, unitPurchase.unitPrice) : null;
  const total = unitPurchase ? formatMoney(intl, unitPurchase.lineTotal) : null;

  const seats = unitPurchase?.seats;

  const listingTitle = transaction?.listing?.attributes?.title || transaction?.attributes?.protectedData?.listingTitle || '';
  const titleAfterHyphen = getTitleAfterHyphen(listingTitle);

  const publicData = transaction?.listing?.attributes?.publicData || {};
  const elopementEp = Number(publicData?.elopementEp);

  let message = null;
  if (elopementEp === 1 || elopementEp === 2) {
    message = '30 Guests';
  } else if (isMultipleDay && titleAfterHyphen) {
    const count = seats || (quantity ? parseInt(quantity, 10) : 1);
    message = count === 1 ? titleAfterHyphen : `${count} ${titleAfterHyphen}`;
  } else if (seats) {
    const guestLabel = seats === 1 ? 'guest' : 'guests';
    message = `${unitPrice} x ${seats} ${guestLabel}`;
  } else if (isMultipleDay && quantity) {
    const count = parseInt(quantity, 10);
    const guestLabel = count === 1 ? 'guest' : 'guests';
    message = `${unitPrice} x ${count} ${guestLabel}`;
  } else {
    message = <FormattedMessage id={translationKey} values={{ unitPrice, quantity }} />;
  }

  return (quantity || seats) && total ? (
    <div className={classNames(css.lineItem, css.bookingPeriod)}>
      <span className={css.itemLabel}>{message}</span>
      <span className={css.itemValue}>{total}</span>
    </div>
  ) : null;
};

export default LineItemBasePriceMaybe;
